import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks';
import Modal, { ModalButton, modalInputClass } from '@/components/common/Modal';
import { toggleEditStake } from '@/store';
import { useForm, useFieldArray } from 'react-hook-form';
import { StakeOption, useGetStakesQuery, useUpdateStakesMutation } from '@/store/api/betApi';
import { isGuestUser } from '@/utils';

interface StakeForm {
  chips: StakeOption[];
}

const SetButtonValueModal = () => {
  const { editStakeOpen } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();

  /* ---------------- API ---------------- */
  const isGuest = isGuestUser();
  const { data: stakeData, isFetching } = useGetStakesQuery(undefined, { skip: isGuest });
  const [updateStakes, { isLoading: isUpdating }] = useUpdateStakesMutation();

  /* ---------------- FORM ---------------- */
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StakeForm>({
    defaultValues: { chips: [] },
  });

  const { fields } = useFieldArray({
    control,
    name: 'chips',
  });

  /* ---------------- Populate Data ---------------- */
  useEffect(() => {
    if (stakeData?.stakeSet) {
      reset({ chips: stakeData.stakeSet });
    }
  }, [stakeData, reset]);

  /* ---------------- Reset on Close ---------------- */
  useEffect(() => {
    if (!editStakeOpen) {
      reset();
    }
  }, [editStakeOpen, reset]);

  /* ---------------- Submit ---------------- */
  const onSubmit = async (data: StakeForm) => {
    try {
      await updateStakes({ data: data.chips }).unwrap();
      dispatch(toggleEditStake());
    } catch (err) {
      console.error(err);
    }
  };

  const close = () => dispatch(toggleEditStake());
  const inputClass = (hasError: boolean) =>
    `${modalInputClass} h-10 ${hasError ? 'border-danger-red focus:border-danger-red' : ''}`;

  return (
    <Modal
      open={editStakeOpen}
      onClose={close}
      title="Set Button Value"
      description="Quick-stake buttons shown in your bet slip"
      size="md"
      footer={
        <>
          <ModalButton variant="secondary" onClick={close}>
            Cancel
          </ModalButton>
          <ModalButton type="submit" form="stake-buttons-form" disabled={isUpdating}>
            {isUpdating ? 'Updating...' : 'Update'}
          </ModalButton>
        </>
      }
    >
      <form id="stake-buttons-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-2 grid grid-cols-2 gap-x-4 text-xs font-medium text-shell-muted">
          <span>Button label</span>
          <span>Stake value</span>
        </div>

        {isFetching ? (
          <p className="py-6 text-center text-sm text-shell-muted">Loading...</p>
        ) : (
          <div className="space-y-2">
            {fields.map((field, i) => (
              <div key={field.id} className="grid grid-cols-2 gap-x-4">
                <div>
                  <input
                    {...register(`chips.${i}.label` as const, {
                      required: 'Label is required',
                      maxLength: { value: 6, message: 'Maximum 6 characters allowed' },
                      validate: (value) =>
                        /^[a-zA-Z0-9]*$/.test(value) || 'Only alphanumeric characters allowed',
                      onChange: (e) => {
                        e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                      },
                    })}
                    className={inputClass(!!errors?.chips?.[i]?.label)}
                  />
                  {errors?.chips?.[i]?.label && (
                    <span className="mt-1 block text-xs text-danger-red">
                      {errors.chips[i]?.label?.message}
                    </span>
                  )}
                </div>
                <div>
                  <input
                    type="number"
                    min={1}
                    step="1"
                    inputMode="numeric"
                    {...register(`chips.${i}.value` as const, {
                      valueAsNumber: true,
                      required: 'Value is required',
                      min: { value: 1, message: 'Value must be greater than 0' },
                    })}
                    onKeyDown={(e) => {
                      if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault();
                    }}
                    className={inputClass(!!errors?.chips?.[i]?.value)}
                  />
                  {errors?.chips?.[i]?.value && (
                    <span className="mt-1 block text-xs text-danger-red">
                      {errors.chips[i]?.value?.message}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </form>
    </Modal>
  );
};

export default SetButtonValueModal;
